import React, { useState, useEffect, useRef } from 'react';
import { KendoReactComboBox } from '@Shared.Angular/kendo.react/KendoReactComboBox';
import {
  ComboBoxChangeEvent,
  ComboBoxFilterChangeEvent
} from '@progress/kendo-react-dropdowns';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { BusinessTenant } from '@Client/runner.services/auth.service';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';

type Props = {
  activeTenantId: string;
  userBusinessId: string;
  onClose: () => void;
};

export const ChangeTenantModal = (props: Props) => {
  const { activeTenantId, userBusinessId, onClose } = props;

  const [waitingForApi, setWaitingForApi] = useState(true);
  const [allTenants, setAllTenants] = useState<BusinessTenant[]>([]);
  const [filteredTenants, setFilteredTenants] = useState<BusinessTenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<BusinessTenant>();
  const [activeTenantName, setActiveTenantName] = useState<string>();
  const [userBusinessName, setUserBusinessName] = useState<string>();

  const comboBoxRef = useRef<HTMLDivElement>(null);

  const authService = getService('authService');

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const loadedTenants = await authService.getTenants();
      const sortedTenants = [...loadedTenants].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setAllTenants(sortedTenants);
      setFilteredTenants(sortedTenants);

      const selected = sortedTenants.find(
        (tenant) => tenant.id === activeTenantId
      );
      setSelectedTenant(selected);
      setActiveTenantName(selected?.name);

      const userBusiness = sortedTenants.find(
        (tenant) => tenant.id === userBusinessId
      );
      setUserBusinessName(userBusiness?.name);
    } finally {
      setWaitingForApi(false);
      requestAnimationFrame(() => {
        const inputElement: HTMLInputElement | null | undefined =
          comboBoxRef.current?.querySelector<HTMLInputElement>('input');
        inputElement?.focus();
        inputElement?.select();
      });
    }
  };

  const changeTenant = (newTenantId: Guid) => {
    setWaitingForApi(true);

    authService.changeTenant(newTenantId).then(
      () => {
        onClose();
        location.reload();
      },
      () => {
        setWaitingForApi(false);
      }
    );
  };

  const selectTenant = (event: ComboBoxChangeEvent) => {
    const tenant = event.value as BusinessTenant;
    setSelectedTenant(tenant);
  };

  const filterTenants = (event: ComboBoxFilterChangeEvent) => {
    const searchTerm = event.filter.value?.toLowerCase();
    if (!searchTerm) {
      setFilteredTenants([...allTenants]);
      return;
    }
    setFilteredTenants(
      allTenants.filter(
        (tenant) => tenant.name?.toLowerCase().indexOf(searchTerm) > -1
      )
    );
  };

  const revertToUserBusiness = () => {
    changeTenant(userBusinessId as Guid);
  };

  const switchToSelectedTenant = () => {
    changeTenant(selectedTenant.id as Guid);
  };

  const isSwitchDisabled =
    waitingForApi || selectedTenant?.id === activeTenantId || !selectedTenant;

  return (
    <Modal
      id="change-tenant-modal"
      title="Switch Tenant"
      actionButtonText="Switch Tenant"
      onActionClick={switchToSelectedTenant}
      isActionDisabled={isSwitchDisabled}
      secondaryButtonText={`Return to ${userBusinessName}`}
      onSecondaryButtonClick={revertToUserBusiness}
      isSecondaryButtonDisabled={activeTenantId === userBusinessId}
      onClose={onClose}
      onCancelClick={onClose}
      size="md"
    >
      <div className="content">
        <div>
          <p>
            You are currently working in business <em>{activeTenantName}</em>.
          </p>
          <p>
            Choose a different business from the list below and click "Switch
            tenant" to change.
          </p>
        </div>
        <div>
          {allTenants.length > 0 && (
            <div ref={comboBoxRef}>
              <KendoReactComboBox
                inputId="businesses"
                className="tenant-combobox"
                data={filteredTenants}
                textField="name"
                dataItemKey="id"
                placeholder="Select Tenant"
                value={selectedTenant}
                onChange={selectTenant}
                filterable={true}
                onFilterChange={filterTenants}
                clearButton={true}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
